// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { mergeWithCss, safeJsonParse, xMask } from "@ces/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { shallowEqual } from "react-redux";

interface MiniSignInProps {
    viewRepLink?: LinkFieldValue;
    linkGroups?: {
        title: string;
        links: (
            | {
                  title: string;
                  url: string;
                  type?: string | undefined;
                  id: string;
                  parentId?: string | undefined;
                  excludeFromNavigation?: boolean | undefined;
                  children?: PageLinkModel[] | undefined;
                  openInNewWindow?: boolean;
                  queryString?: string;
              }
            | undefined
        )[];
    }[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkItems?: {
        wrapper?: InjectableCss;
        linksContainer?: GridContainerProps;
        linkWrapper?: GridItemProps;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        subTitle?: TypographyPresentationProps;
        infoColumnContainer?: InjectableCss;
        name?: TypographyPresentationProps;
        label?: TypographyPresentationProps;
        accountLink?: LinkPresentationProps;
        viewLink?: LinkPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        signOutButton?: LinkPresentationProps;
        changeShipTo?: LinkPresentationProps;
    };
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const MiniMyAccount: FC<Props> = ({ viewRepLink, linkGroups, onSignOutHandler, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const { isInShell } = useShellContext();
    const history = useHistory();
    const { url: viewRepUrl } = useGetLink(viewRepLink!);

    const onClickLink = (url: string) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const {
        currentBillTo,
        displayChangeCustomerLink,
        isPunchOutSession,
        changeCustomerUrl,
        csrInformation,
        salesPersonInformation,
        address,
    } = useAppSelector(state => {
        const session = getSession(state);
        const { csrInformation, salesPersonInformation } = session.properties;
        const { fulfillmentMethod, shipToId, pickUpWarehouse } = session;
        const shipTo = getShipToState(state, shipToId).value;
        return {
            currentBillTo: getBillToState(state, session.billToId).value,
            displayChangeCustomerLink: session.displayChangeCustomerLink,
            isPunchOutSession: getIsPunchOutSession(state),
            changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            salesPersonInformation: safeJsonParse(salesPersonInformation),
            csrInformation: safeJsonParse(csrInformation),
            address: fulfillmentMethod === "Ship" ? shipTo : pickUpWarehouse,
        };
    }, shallowEqual);

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    <GridContainer {...styles.linkItems?.linksContainer}>
                        {linkGroup.links.map(
                            link =>
                                link?.url && (
                                    <GridItem key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                        <Link
                                            onClick={() => {
                                                hideAll();
                                            }}
                                            href={`${link?.url}${link.queryString || ""}`}
                                            {...styles.linkItems?.link}
                                            target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                            aria-label={link?.title}
                                        >
                                            {link?.title}
                                        </Link>
                                    </GridItem>
                                ),
                        )}
                    </GridContainer>
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>
                        {translate("Customer #{0}", currentBillTo?.customerNumber || "")}
                    </Typography>
                </StyledWrapper>
                {linksSection}
                <StyledWrapper {...styles.personalInfo?.wrapper}>
                    <Typography {...styles.personalInfo?.title}>{translate("Profile Information")}</Typography>
                    <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                        {salesPersonInformation && (
                            <StyledWrapper {...styles.personalInfo?.infoColumnContainer}>
                                <Typography {...styles.personalInfo?.subTitle}>{translate("Outside Sales")}</Typography>
                                <Typography {...styles.personalInfo?.name}>{salesPersonInformation.name}</Typography>
                                {salesPersonInformation.phone && (
                                    <Link
                                        href={`tel:${salesPersonInformation.phone}`}
                                        {...styles.personalInfo?.accountLink}
                                    >
                                        {xMask.phoneNumberUS(salesPersonInformation.phone)}
                                    </Link>
                                )}
                                {salesPersonInformation.email && (
                                    <Link
                                        href={`mailto:${salesPersonInformation.email}`}
                                        {...styles.personalInfo?.accountLink}
                                    >
                                        {salesPersonInformation.email}
                                    </Link>
                                )}
                                {viewRepUrl && (
                                    <Link onClick={() => onClickLink(viewRepUrl)} {...styles.personalInfo?.viewLink}>
                                        {translate("View Your Reps")}
                                    </Link>
                                )}
                            </StyledWrapper>
                        )}
                        {csrInformation && (
                            <StyledWrapper {...styles.personalInfo?.infoColumnContainer}>
                                <Typography {...styles.personalInfo?.subTitle}>{translate("Inside Sales")}</Typography>
                                <Typography {...styles.personalInfo?.name}>{csrInformation.name}</Typography>
                                {csrInformation.phone && (
                                    <Link href={`tel:${csrInformation.phone}`} {...styles.personalInfo?.accountLink}>
                                        {xMask.phoneNumberUS(csrInformation.phone)}
                                    </Link>
                                )}
                                {csrInformation.email && (
                                    <Link href={`mailto:${csrInformation.email}`} {...styles.personalInfo?.accountLink}>
                                        {csrInformation.email}
                                    </Link>
                                )}
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.personalInfo?.infoColumnContainer}>
                            <Typography {...styles.personalInfo?.subTitle}>{translate("Shipping Address")}</Typography>
                            {[address?.address1, address?.address2].map(address =>
                                address ? (
                                    <Typography {...styles.personalInfo?.label} key={address}>
                                        {address}
                                    </Typography>
                                ) : null,
                            )}
                            <Typography {...styles.personalInfo?.label}>
                                {address?.city},{" "}
                                {typeof address?.state === "string" ? address?.state : address?.state?.abbreviation}{" "}
                                {address?.postalCode}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionSection?.wrapper}>
                {displayChangeCustomerLink && (
                    <Link
                        onClick={() => {
                            changeCustomerUrl && history.push(changeCustomerUrl);
                            hideAll();
                        }}
                        aria-label="Change Ship-To"
                        {...styles.actionSection?.changeShipTo}
                        data-test-selector="header-changeCustomer"
                    >
                        {translate("Change Ship-To")}
                    </Link>
                )}
                <Link
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                        hideAll();
                    }}
                    aria-label="Log Out"
                    data-test-selector="header-logOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Log Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
